import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function EditBooking() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [generatedText, setGeneratedText] = useState('');

  useEffect(() => {
    loadBooking();
  }, [id]);

  const loadBooking = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setForm({
        customerName: data.customerName || '',
        contactNumber: data.contactNumber || '',
        customerEmail: data.customerEmail || '',
        groupSize: data.groupSize || 1,
        groupMembers: data.groupMembers || [],
        trekName: data.trekName || '',
        duration: data.duration || '',
        startDate: data.startDate ? new Date(data.startDate + 'T00:00:00') : null,
        endDate: data.endDate ? new Date(data.endDate + 'T00:00:00') : null,
        pickupPoint: data.pickupPoint || { name: '', time: '' },
        dropoffPoint: data.dropoffPoint || { name: '', time: '' },
        pricing: data.pricing || {},
        paymentMode: data.paymentMode || 'UPI',
        paymentStatus: data.paymentStatus || 'pending',
        doubleSharingOpted: (data.doubleSharing && data.doubleSharing.opted) || false,
        doubleSharingDetails: (data.doubleSharing && data.doubleSharing.details) || '',
        addons: data.addons || [],
      });
    } catch (err) {
      toast.error('Failed to load booking');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.customerName || !form.trekName) {
      toast.error('Customer name and trek name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customerName: form.customerName,
        contactNumber: form.contactNumber,
        customerEmail: form.customerEmail,
        groupSize: Number(form.groupSize),
        groupMembers: form.groupMembers,
        trekName: form.trekName,
        duration: form.duration,
        startDate: form.startDate ? `${form.startDate.getFullYear()}-${String(form.startDate.getMonth()+1).padStart(2,'0')}-${String(form.startDate.getDate()).padStart(2,'0')}` : '',
        endDate: form.endDate ? `${form.endDate.getFullYear()}-${String(form.endDate.getMonth()+1).padStart(2,'0')}-${String(form.endDate.getDate()).padStart(2,'0')}` : '',
        pickupPoint: form.pickupPoint,
        dropoffPoint: form.dropoffPoint,
        pricing: form.pricing,
        paymentMode: form.paymentMode,
        doubleSharing: { opted: form.doubleSharingOpted, details: form.doubleSharingOpted ? form.doubleSharingDetails : '' },
        addons: form.addons.filter(a => a.name && a.name.trim()),
      };
      await api.put(`/bookings/${id}`, payload);
      toast.success('Booking updated successfully!');

      // Regenerate the booking confirmation text with the updated details
      const { data: doc } = await api.post(`/documents/booking-confirmation/${id}`);
      setGeneratedText(doc.text);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update booking');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedText);
    toast.success('Copied to clipboard!');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Loading booking...</p></div>;
  if (!form) return null;

  if (generatedText) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-green-800">✅ Booking Updated Successfully</h2>
          <p className="text-green-700 mt-1">Booking ID: <strong>{id}</strong></p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Booking Confirmation Text</h3>
            <div className="space-x-2">
              <button onClick={copyToClipboard} className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700">
                📋 Copy to Clipboard
              </button>
              <button onClick={() => navigate(-1)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-200">
                ← Back
              </button>
            </div>
          </div>
          <textarea value={generatedText} onChange={e => setGeneratedText(e.target.value)}
            className="w-full h-[600px] p-4 border rounded-lg font-mono text-sm bg-gray-50 resize-none" />
        </div>
      </div>
    );
  }

  const groupSize = Number(form.groupSize) || 1;

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">✏️ Edit Booking - {id}</h1>
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
      </div>

      <div className="space-y-6">
        {/* Customer Details */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Customer Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
              <input type="text" value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
              <input type="tel" value={form.contactNumber} onChange={e => setForm(f => ({ ...f, contactNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Email</label>
              <input type="email" value={form.customerEmail} onChange={e => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group Size</label>
              <input type="number" min="1" value={form.groupSize} onChange={e => {
                const size = Number(e.target.value) || 1;
                const members = Array.from({ length: Math.max(0, size) }, (_, i) => form.groupMembers[i] || '');
                setForm(f => ({ ...f, groupSize: e.target.value, groupMembers: members }));
              }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
          </div>
          {groupSize > 1 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Group Members</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {form.groupMembers.map((member, i) => (
                  <input key={i} type="text" value={member} placeholder={`Member ${i + 1}`}
                    onChange={e => { const m = [...form.groupMembers]; m[i] = e.target.value; setForm(f => ({ ...f, groupMembers: m })); }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Trek Details */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Trek Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trek Name</label>
              <input type="text" value={form.trekName} onChange={e => setForm(f => ({ ...f, trekName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <input type="text" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. 4D/3N" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
              <select value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none">
                <option value="UPI">UPI</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <DatePicker selected={form.startDate} onChange={date => setForm(f => ({ ...f, startDate: date }))}
                dateFormat="yyyy-MM-dd" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <DatePicker selected={form.endDate} onChange={date => setForm(f => ({ ...f, endDate: date }))}
                dateFormat="yyyy-MM-dd" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
          </div>
        </section>

        {/* Pickup & Dropoff */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Pickup & Drop</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
              <input type="text" value={form.pickupPoint.name} onChange={e => setForm(f => ({ ...f, pickupPoint: { ...f.pickupPoint, name: e.target.value } }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
              <input type="text" value={form.pickupPoint.time} onChange={e => setForm(f => ({ ...f, pickupPoint: { ...f.pickupPoint, time: e.target.value } }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. 6:00 AM" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Drop Location</label>
              <input type="text" value={form.dropoffPoint.name} onChange={e => setForm(f => ({ ...f, dropoffPoint: { ...f.dropoffPoint, name: e.target.value } }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Drop Time</label>
              <input type="text" value={form.dropoffPoint.time} onChange={e => setForm(f => ({ ...f, dropoffPoint: { ...f.dropoffPoint, time: e.target.value } }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. 8:00 PM" />
            </div>
          </div>
        </section>

        {/* Double Sharing & Add-ons */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🏨 Double Sharing & Add-ons</h2>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.doubleSharingOpted}
              onChange={e => setForm(f => ({ ...f, doubleSharingOpted: e.target.checked, doubleSharingDetails: e.target.checked ? f.doubleSharingDetails : '' }))}
              className="rounded border-gray-300" />
            <span className="text-sm font-medium text-gray-700">Customer opted for Double Sharing stay</span>
          </label>
          {form.doubleSharingOpted && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Double Sharing Details</label>
              <input type="text" value={form.doubleSharingDetails} onChange={e => setForm(f => ({ ...f, doubleSharingDetails: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="e.g. Extra ₹1,500 per person for double sharing room" />
            </div>
          )}

          <div className="mt-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Add-ons <span className="text-xs text-gray-400">(e.g. Delhi Pickup, Delhi Drop)</span></label>
            <div className="space-y-2">
              {form.addons.map((addon, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={addon.name}
                    onChange={e => { const addons = [...form.addons]; addons[i] = { ...addons[i], name: e.target.value }; setForm(f => ({ ...f, addons })); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Add-on name (e.g. Delhi Pickup)" />
                  <input type="text" value={addon.details}
                    onChange={e => { const addons = [...form.addons]; addons[i] = { ...addons[i], details: e.target.value }; setForm(f => ({ ...f, addons })); }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-sm" placeholder="Details / cost (optional)" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, addons: f.addons.filter((_, idx) => idx !== i) }))}
                    className="text-red-500 hover:text-red-700 px-2">🗑️</button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setForm(f => ({ ...f, addons: [...f.addons, { name: '', details: '' }] }))}
              className="mt-2 text-sm text-primary-600 hover:underline">+ Add Add-on</button>
          </div>
        </section>

        {/* Pricing */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Pricing</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Per Person Cost (₹)</label>
              <input type="number" value={form.pricing.perPersonCost || ''} onChange={e => {
                const pp = Number(e.target.value) || 0;
                const misc = form.pricing.miscCharges || 0;
                const total = pp * groupSize + misc;
                const advPP = form.pricing.advancePerPerson || 0;
                const totalAdv = advPP * groupSize;
                setForm(f => ({ ...f, pricing: { ...f.pricing, perPersonCost: pp, totalCost: total, pendingPerPerson: pp - advPP, totalPending: total - totalAdv } }));
              }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Misc Charges (₹)</label>
              <input type="number" value={form.pricing.miscCharges || ''} onChange={e => {
                const misc = Number(e.target.value) || 0;
                const pp = form.pricing.perPersonCost || 0;
                const total = pp * groupSize + misc;
                const advPP = form.pricing.advancePerPerson || 0;
                const totalAdv = advPP * groupSize;
                setForm(f => ({ ...f, pricing: { ...f.pricing, miscCharges: misc, totalCost: total, totalPending: total - totalAdv } }));
              }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost (₹)</label>
              <input type="number" value={form.pricing.totalCost || ''} readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advance Per Person (₹)</label>
              <input type="number" value={form.pricing.advancePerPerson || ''} onChange={e => {
                const advPP = Number(e.target.value) || 0;
                const totalAdv = advPP * groupSize;
                const pp = form.pricing.perPersonCost || 0;
                const misc = form.pricing.miscCharges || 0;
                const total = pp * groupSize + misc;
                setForm(f => ({ ...f, pricing: { ...f.pricing, advancePerPerson: advPP, totalAdvance: totalAdv, pendingPerPerson: pp - advPP, totalPending: total - totalAdv } }));
              }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Advance (₹)</label>
              <input type="number" value={form.pricing.totalAdvance || ''} readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Pending (₹)</label>
              <input type="number" value={form.pricing.totalPending || ''} readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Advance Due Date</label>
              <input type="text" value={form.pricing.advanceDueDate || ''} onChange={e => setForm(f => ({ ...f, pricing: { ...f.pricing, advanceDueDate: e.target.value } }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none" placeholder="YYYY-MM-DD" />
            </div>
          </div>
        </section>

        {/* Save Button */}
        <div className="flex gap-3">
          <button onClick={handleSave} disabled={saving}
            className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50">
            {saving ? 'Saving...' : '💾 Save Changes'}
          </button>
          <button onClick={() => navigate(-1)} className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
